import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiSearch,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiToggleLeft,
  FiToggleRight,
  FiFilter,
  FiAlertCircle,
  FiX,
  FiExternalLink,
} from "react-icons/fi";
import {
  FaSpinner,
  FaTimes,
  FaCopy,
  FaCalendarAlt,
  FaLink,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ThemeMaker from "../thememaker";
import { useProfile } from "../../context/ProfileContext";
import { formManagementService } from "../../services/formManagementService";
import CreateFormDrawer from "./CreateFormDrawer";
import FormBuilderPage from "./FormBuilderPage";
import FormPreviewPanel from "./FormPreviewPanel";
import { FORM_CATEGORIES } from "./boardingFormTemplate";
import { buildFormPublicLink } from "../../utils/formLinkHelper";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  active: "bg-green-100 text-green-700",
  draft: "bg-yellow-100 text-yellow-700",
  inactive: "bg-gray-100 text-gray-500",
};

const STATUS_LABELS = { active: "Active", draft: "Draft", inactive: "Inactive" };

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
        STATUS_COLORS[status] || STATUS_COLORS.draft
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ onCreateNew }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-20 h-20 bg-[#1B4582]/10 rounded-2xl flex items-center justify-center mb-4">
        <FiEdit2 className="w-9 h-9 text-[#1B4582]" />
      </div>
      <h3 className="text-base font-semibold text-gray-700 mb-2">No forms yet</h3>
      <p className="text-sm text-gray-500 mb-6 max-w-xs">
        Create your first form from a template (boarding, onboarding, grooming) or build a custom form.
      </p>
      <button
        onClick={onCreateNew}
        className="flex items-center gap-2 px-5 py-2.5 bg-[#1B4582] text-white rounded-xl text-sm font-semibold hover:bg-[#163a6e] shadow-md transition-all"
      >
        <FiPlus className="w-4 h-4" />
        Create New Form
      </button>
    </div>
  );
}

// ─── Delete confirmation ──────────────────────────────────────────────────────
function DeleteModal({ open, formName, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
            <FiAlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-800">Delete Form</h3>
        </div>
        <p className="text-sm text-gray-600 mb-5">
          Are you sure you want to delete <strong>"{formName}"</strong>? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            Delete Form
          </button>
        </div>
      </div>
    </div>
  );
}

function FormViewDrawer({ form, onClose }) {
  const { profileData, rawServiceCategory } = useProfile();
  const [detailedForm, setDetailedForm] = useState(form);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");

  useEffect(() => {
    setDetailedForm(form);
    const formId = form?._id || form?.id || form?.backendId || form?.form_id;
    const hasFieldsOrSections =
      (form?.questions && form.questions.length > 0) ||
      (form?.sections && form.sections.length > 0);

    if (formId && !hasFieldsOrSections) {
      setLoadingDetails(true);
      formManagementService
        .getFormById(formId)
        .then((res) => {
          if (res) {
            setDetailedForm((prev) => ({ ...prev, ...res }));
          }
        })
        .catch((err) => {
          console.error("Error loading full form details:", err);
        })
        .finally(() => {
          setLoadingDetails(false);
        });
    }
  }, [form]);

  if (!form) return null;

  const currentForm = detailedForm || form;

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getServiceLabel = (svcId) => {
    const target = String(svcId);
    const legacyNames = {
      boarding: "Boarding",
      grooming: "Grooming",
      pet_vet: "Veterinary",
      veterinary: "Veterinary",
      other: "Other",
    };
    if (legacyNames[target]) return legacyNames[target];
    const cat = (rawServiceCategory || []).find((c) =>
      [c._id, c.service_category_id, c.id]
        .filter(Boolean)
        .some((v) => String(v) === target),
    );
    return (
      cat?.service_category_name || cat?.name || cat?.categoryname || svcId
    );
  };

  const services = Array.isArray(currentForm.services)
    ? currentForm.services
    : [];

  const status =
    currentForm.enabled !== false && currentForm.status !== "inactive"
      ? "Enabled"
      : "Disabled";
  const totalResponses = currentForm.totalResponses || 0;
  const dateField =
    currentForm.createdAt || currentForm.created_at || currentForm.createdDate;
  const createdDate = dateField
    ? new Date(dateField).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "N/A";

  // ── Form public link ──────────────────────────────────────────────────────
  const vendorId = profileData?._id || profileData?.id || "";
  const formId =
    currentForm._id ||
    currentForm.backendId ||
    currentForm.form_id ||
    currentForm.id ||
    "";
  const formLink = buildFormPublicLink(vendorId, formId, currentForm.formLink);

  const copyLink = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(formLink);
    }
    setCopied(true);
    toast.success("Form link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Branding ──────────────────────────────────────────────────────────────
  const businessName =
    profileData?.profile?.companyName ||
    profileData?.formData?.companyName ||
    profileData?.profile?.name ||
    "Your Business";
  const businessLogo =
    profileData?.photo?.profilePhoto || profileData?.photo?.shop?.[0] || null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-[65] backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl lg:max-w-5xl bg-gray-50 shadow-2xl z-[66] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Management Panel
            </span>
            <h2 className="text-xl font-bold text-gray-900 mt-0.5">
              {currentForm.formName || currentForm.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="text-lg" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* ── METRICS & SUMMARY ───────────────────────────────────────────── */}
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4 flex items-center gap-2 border-b border-gray-100 pb-2">
              <FaCheckCircle className="text-emerald-500" /> FORM METRICS &amp;
              SUMMARY
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Status */}
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span className="block text-xs font-medium text-gray-500 uppercase">
                  STATUS
                </span>
                <span
                  className={`inline-flex items-center mt-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold ${
                    currentForm.enabled !== false &&
                    currentForm.status !== "inactive"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {status}
                </span>
              </div>

              {/* Requirement */}
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span className="block text-xs font-medium text-gray-500 uppercase">
                  REQUIREMENT
                </span>
                <span
                  className={`inline-flex items-center mt-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold ${
                    currentForm.requirement === "mandatory"
                      ? "bg-red-100 text-red-800"
                      : "bg-emerald-100 text-emerald-800"
                  }`}
                >
                  {currentForm.requirement === "mandatory"
                    ? "Mandatory"
                    : "Optional"}
                </span>
              </div>

              {/* Responses */}
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span className="block text-xs font-medium text-gray-500 uppercase">
                  RESPONSES
                </span>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-xl font-bold text-gray-900 leading-none">
                    {totalResponses}
                  </span>
                  <span className="text-[10px] font-medium text-gray-400 uppercase">
                    TOTAL
                  </span>
                </div>
              </div>

              {/* Created */}
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span className="block text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                  <FaCalendarAlt className="text-gray-400" /> CREATED
                </span>
                <span className="block text-xs font-semibold text-gray-700 mt-2 truncate">
                  {createdDate}
                </span>
              </div>

              {/* Services */}
              <div className="col-span-2 sm:col-span-4 border-t border-gray-100 pt-3">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  ASSOCIATED SERVICES
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {services.length > 0 ? (
                    services.map((svcId) => (
                      <span
                        key={svcId}
                        className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200"
                      >
                        {getServiceLabel(svcId)}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs italic text-gray-400">
                      No services explicitly linked
                    </span>
                  )}
                </div>
              </div>

              {/* Form Behaviour */}
              <div className="col-span-2 sm:col-span-4 border-t border-gray-100 pt-3">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  FORM BEHAVIOUR
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {currentForm.formFrequency === "every_time" && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                      Every time a booking is created
                    </span>
                  )}
                  {currentForm.formFrequency === "first_time" && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                      Only the first time an appointment is booked
                    </span>
                  )}
                  {currentForm.formFrequency === "expires_12months" && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                      Expires and must be completed when the selected validity
                      period ends
                      {currentForm.validityPeriod && (
                        <span className="ml-1 font-bold">
                          ({currentForm.validityPeriod} months)
                        </span>
                      )}
                    </span>
                  )}
                  {(currentForm.formFrequency === "not_required" ||
                    currentForm.requirement === "not_required") && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                      Form is not required
                    </span>
                  )}
                  {!currentForm.formFrequency && (
                    <span className="text-xs italic text-gray-400">
                      Every time a booking is created
                    </span>
                  )}
                </div>
              </div>

              {/* ── Form Link with Copy Button ── only when form is "not required" ── */}
              {(currentForm.formFrequency === "not_required" ||
                currentForm.requirement === "not_required") && (
                <div className="col-span-2 sm:col-span-4 border-t border-gray-100 pt-3">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaLink className="text-blue-500" /> FORM LINK
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={formLink}
                      className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-700 font-mono truncate focus:outline-none focus:border-blue-400 selection:bg-blue-100"
                    />
                    <button
                      onClick={copyLink}
                      className={`flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-xs font-semibold transition-all shrink-0 shadow-sm ${
                        copied
                          ? "bg-emerald-600 hover:bg-emerald-700"
                          : "bg-[#1B4582] hover:bg-[#163a6e]"
                      }`}
                    >
                      {copied ? (
                        <FaCheckCircle className="text-sm" />
                      ) : (
                        <FaCopy />
                      )}
                      {copied ? "Copied!" : "Copy"}
                    </button>
                    <a
                      href={formLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold transition-colors shrink-0"
                    >
                      Open ↗
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Customer-facing preview ─────────────────────────────────────── */}
          {/* ── Customer-facing preview ─────────────────────────────────────── */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* ── Desktop / Mobile toggle ── */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">
                Customer Preview
              </span>

              <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setPreviewMode("desktop")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                    previewMode === "desktop"
                      ? "bg-[#1B4582] text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <path d="M8 21h8M12 17v4" />
                  </svg>
                  Desktop
                </button>

                <button
                  type="button"
                  onClick={() => setPreviewMode("mobile")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                    previewMode === "mobile"
                      ? "bg-[#1B4582] text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="7" y="2" width="10" height="20" rx="2" />
                    <path d="M12 18h.01" />
                  </svg>
                  Mobile
                </button>
              </div>
            </div>

            <div
              className={
                previewMode === "mobile"
                  ? "flex justify-center bg-gray-50 py-6 px-3"
                  : "bg-white"
              }
            >
              <div
                className={
                  previewMode === "mobile" ? "w-[390px] max-w-full" : "w-full"
                }
              >
                {loadingDetails ? (
                  <div className="py-12 text-center bg-white border border-gray-200 rounded-2xl">
                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-xs text-gray-400">
                      Loading customer preview…
                    </p>
                  </div>
                ) : (
                  <FormPreviewPanel
                    formName={currentForm.formName || currentForm.name}
                    formDescription={
                      currentForm.formDescription || currentForm.description
                    }
                    vendorName={businessName}
                    vendorLogo={businessLogo}
                    sections={
                      Array.isArray(currentForm.sections) &&
                      currentForm.sections.length
                        ? currentForm.sections
                        : (currentForm.questions || []).reduce((secs, q) => {
                            if (
                              q.answerType === "section_heading" ||
                              q.answerType === "section_header"
                            ) {
                              secs.push({
                                id: q._id || q.id,
                                sectionName: q.question,
                                sectionDescription: q.description || "",
                                fields: [],
                              });
                              return secs;
                            }
                            if (!secs.length) {
                              secs.push({
                                id: "sec-default",
                                sectionName: "Details",
                                fields: [],
                              });
                            }
                            if (q.enabled === false) return secs;
                            secs[secs.length - 1].fields.push({
                              id: q._id || q.id,
                              label: q.question,
                              fieldType: q.answerType || "short_text",
                              required: q.isRequired,
                              enabled: q.enabled !== false,
                              options: q.options || [],
                              defaultValue: q.defaultValue,
                              helpText: "",
                            });
                            return secs;
                          }, [])
                    }
                    previewMode={previewMode}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}




// ─── Main Page ────────────────────────────────────────────────────────────────


export default function FormManagement() {
  const { profileData } = useProfile();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  // Drawer / builder state
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [builderForm, setBuilderForm] = useState(null); // null = list view, object = builder view
  const [editingFormId, setEditingFormId] = useState(null); // for existing form edits
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // View drawer
  const [viewingForm, setViewingForm] = useState(null);

  // Delete modal
  const [deleteModal, setDeleteModal] = useState({ open: false, form: null });

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const data = await formManagementService.getAllForms();
      setForms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching forms:", err);
      toast.error("Failed to load forms.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // ── Filtering ─────────────────────────────────────────────────────────────────

  const filteredForms = forms.filter((f) => {
    const name = (f.formName || f.name || "").toLowerCase();
    const matchSearch = !searchTerm || name.includes(searchTerm.toLowerCase());
    const matchType = filterType === "all" || f.formType === filterType || f.formCategory === filterType;
    const matchStatus =
      filterStatus === "all" || (f.status || "draft") === filterStatus;
    return matchSearch && matchType && matchStatus;
  });

  // ── Create form handlers ──────────────────────────────────────────────────────

  const handleCreateBoardingForm = (template) => {
    // Template becomes the builder state — not yet persisted
    setBuilderForm({
      ...template,
      formFrequency: template.formFrequency || "every_time",
      validityPeriod: template.validityPeriod || 12,
      requirement: template.requirement || "optional",
      services: template.services || (template.formCategory ? [template.formCategory] : ["General"]),
      _isNew: true,
    });
    setEditingFormId(null);
  };

  const handleCreateCustomForm = (data) => {
    setBuilderForm({
      ...data,
      formFrequency: data.formFrequency || "every_time",
      validityPeriod: data.validityPeriod || 12,
      requirement: data.requirement || "optional",
      services: data.services || (data.formCategory ? [data.formCategory] : []),
      _isNew: true,
    });
    setEditingFormId(null);
  };

  // ── Edit handler ──────────────────────────────────────────────────────────────

  const handleEditForm = async (form) => {
    const formId = form._id || form.id || form.form_id || form.formId || form.backendId;
    let fullForm = form;

    // Try to load full form data (with sections and fields)
    if (formId) {
      try {
        const data = await formManagementService.getFormById(formId);
        if (data && (data.sections || data.formName)) fullForm = data;
      } catch {
        // Fall back to list data
      }
    }

    const resolvedId = formId || fullForm._id || fullForm.id || fullForm.form_id;

    const rawCat = fullForm.formCategory || fullForm.category || "";
    const catMatch = FORM_CATEGORIES.find((c) => c.toLowerCase() === String(rawCat).trim().toLowerCase());
    const cleanCategory = catMatch || "Boarding";

    setBuilderForm({
      _id: resolvedId,
      id: resolvedId,
      formName: fullForm.formName || fullForm.name || "",
      formDescription: fullForm.formDescription || fullForm.description || "",
      formCategory: cleanCategory,
      formType: fullForm.formType || "custom",
      status: fullForm.status || "draft",
      formFrequency: fullForm.formFrequency || "every_time",
      validityPeriod: fullForm.validityPeriod || 12,
      requirement: fullForm.requirement || "optional",
      services: [cleanCategory],
      sections: fullForm.sections || [],
      _isNew: false,
    });
    setEditingFormId(resolvedId);
  };

  // ── Save / Publish ────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!builderForm) return;
    if (!builderForm.formName || !builderForm.formName.trim()) {
      toast.error("Form Name is mandatory.");
      return;
    }
    if (!builderForm.formCategory || !builderForm.formCategory.trim()) {
      toast.error("Category is mandatory.");
      return;
    }

    setSaving(true);
    try {
      const activeId = editingFormId || builderForm._id || builderForm.id || builderForm.form_id;

      const payload = {
        formName: builderForm.formName.trim(),
        formDescription: builderForm.formDescription,
        formCategory: builderForm.formCategory,
        formType: builderForm.formType,
        status: builderForm.status || "draft",
        formFrequency: builderForm.formFrequency || "every_time",
        validityPeriod: builderForm.validityPeriod || 12,
        requirement: builderForm.requirement || "optional",
        services: builderForm.services || (builderForm.formCategory ? [builderForm.formCategory] : []),
        sections: builderForm.sections,
      };

      if (activeId) {
        await formManagementService.updateForm(activeId, payload);
        toast.success("Form saved successfully!");
      } else {
        const created = await formManagementService.createForm(payload);
        const newId = created?._id || created?.id || created?.form_id || created?.formId || created?.data?._id || created?.data?.form_id;
        if (newId) {
          setEditingFormId(newId);
          setBuilderForm((prev) => ({ ...prev, _id: newId, id: newId }));
        }
        toast.success("Form created successfully!");
      }
      await fetchForms();
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to save form. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!builderForm) return;
    if (!builderForm.formName || !builderForm.formName.trim()) {
      toast.error("Form Name is mandatory.");
      return;
    }
    if (!builderForm.formCategory || !builderForm.formCategory.trim()) {
      toast.error("Category is mandatory.");
      return;
    }

    setPublishing(true);
    try {
      const activeId = editingFormId || builderForm._id || builderForm.id || builderForm.form_id;

      const payload = {
        formName: builderForm.formName,
        formDescription: builderForm.formDescription,
        formCategory: builderForm.formCategory,
        formType: builderForm.formType,
        status: "active",
        formFrequency: builderForm.formFrequency || "every_time",
        validityPeriod: builderForm.validityPeriod || 12,
        requirement: builderForm.requirement || "optional",
        services: builderForm.services || (builderForm.formCategory ? [builderForm.formCategory] : []),
        sections: builderForm.sections,
      };

      if (activeId) {
        // Update existing form only — no duplicate creation!
        await formManagementService.updateForm(activeId, payload);
      } else {
        // Create new form once with active status
        await formManagementService.createForm(payload);
      }

      toast.success("Form published successfully!");
      setBuilderForm(null);
      setEditingFormId(null);
      await fetchForms();
    } catch (err) {
      console.error("Publish error:", err);
      toast.error("Failed to publish form.");
    } finally {
      setPublishing(false);
    }
  };

  const handleCancelBuilder = () => {
    setBuilderForm(null);
    setEditingFormId(null);
  };

  // ── Toggle active/inactive ────────────────────────────────────────────────────

  const handleToggleStatus = async (form) => {
    const formId = form._id || form.id;
    if (!formId) return;
    const isActive = (form.status || "draft") === "active";

    try {
      if (isActive) {
        await formManagementService.deactivateForm(formId);
      } else {
        await formManagementService.publishForm(formId);
      }
      toast.success(`Form ${isActive ? "deactivated" : "activated"} successfully.`);
      await fetchForms();
    } catch {
      toast.error("Failed to update form status.");
    }
  };

  // ── Duplicate ─────────────────────────────────────────────────────────────────

  const handleDuplicate = async (form) => {
    const formId = form._id || form.id;
    if (!formId) {
      toast.error("Cannot duplicate — form ID not found.");
      return;
    }
    try {
      await formManagementService.duplicateForm(formId);
      toast.success("Form duplicated.");
      await fetchForms();
    } catch {
      toast.error("Failed to duplicate form.");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    const formId = deleteModal.form?._id || deleteModal.form?.id;
    if (!formId) return;
    try {
      await formManagementService.deleteForm(formId);
      toast.success("Form deleted.");
      setDeleteModal({ open: false, form: null });
      await fetchForms();
    } catch {
      toast.error("Failed to delete form.");
    }
  };

  // ── If builder is active, show builder full-screen ────────────────────────────

  if (builderForm) {
    return (
      <ThemeMaker noPageScroll>
        <div className="flex flex-col h-full overflow-hidden bg-gray-50">
          <FormBuilderPage
            formName={builderForm.formName}
            formDescription={builderForm.formDescription}
            formCategory={builderForm.formCategory}
            formType={builderForm.formType}
            formFrequency={builderForm.formFrequency || "every_time"}
            validityPeriod={builderForm.validityPeriod || 12}
            requirement={builderForm.requirement || "optional"}
            services={builderForm.services || []}
            sections={builderForm.sections || []}
            onSectionsChange={(sections) =>
              setBuilderForm((prev) => ({ ...prev, sections }))
            }
            onFormMetaChange={(meta) =>
              setBuilderForm((prev) => ({ ...prev, ...meta }))
            }
            onSave={handleSave}
            onPublish={handlePublish}
            onCancel={handleCancelBuilder}
            saving={saving}
            publishing={publishing}
            vendorName={
              profileData?.profile?.companyName ||
              profileData?.formData?.companyName ||
              profileData?.profile?.name ||
              "Your Business"
            }
            vendorLogo={
              profileData?.photo?.profilePhoto || profileData?.photo?.shop?.[0] || null
            }
          />
        </div>
      </ThemeMaker>
    );
  }

  // ── Form list view ─────────────────────────────────────────────────────────────

  return (
    <ThemeMaker>
      <div className="w-full p-4 sm:p-6 bg-white rounded-xl shadow-sm border border-gray-100 min-h-full">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Form Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Create and manage forms for your business.
            </p>
          </div>
          <button
            onClick={() => setShowCreateDrawer(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#1B4582] text-white rounded-xl text-sm font-semibold hover:bg-[#163a6e] shadow-md transition-all shrink-0"
          >
            <FiPlus className="w-4 h-4" />
            Create New Form
          </button>
        </div>

        {/* ── Filter bar ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          {/* Search */}
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search forms by name…"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2 text-sm focus:ring-2 focus:ring-[#1B4582] focus:border-transparent"
            />
          </div>

          {/* Type filter */}
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-400 shrink-0 w-4 h-4" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B4582] focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="predefined">Predefined</option>
              <option value="custom">Custom</option>
              {FORM_CATEGORIES.map((cat) => (
                <option key={cat} value={cat.toLowerCase()}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Status filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1B4582] focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FaSpinner className="w-8 h-8 text-[#1B4582] animate-spin mb-3" />
            <p className="text-sm text-gray-400">Loading forms…</p>
          </div>
        ) : filteredForms.length === 0 ? (
          searchTerm || filterType !== "all" || filterStatus !== "all" ? (
            <div className="text-center py-16">
              <p className="text-sm text-gray-500">
                No forms match your search or filter criteria.
              </p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setFilterType("all");
                  setFilterStatus("all");
                }}
                className="text-sm text-[#1B4582] underline mt-2"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <EmptyState onCreateNew={() => setShowCreateDrawer(true)} />
          )
        ) : (
          <>
            {/* ── Table (desktop) ── */}
            <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Form Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Sections
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Fields
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredForms.map((form, idx) => {
                    const formId = form._id || form.id;
                    const sectionCount = (form.sections || []).length;
                    const fieldCount = (form.sections || []).reduce(
                      (acc, s) => acc + (s.fields || []).length,
                      0,
                    );
                    const status = form.status || "draft";
                    const isActive = status === "active";
                    const updatedAt =
                      form.updatedAt || form.updated_at || form.createdAt;

                    return (
                      <tr
                        key={formId || idx}
                        className="hover:bg-gray-50 transition-colors cursor-default"
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-semibold text-gray-800 truncate max-w-[200px]">
                              {form.formName || form.name}
                            </p>
                            {form.formDescription && (
                              <p className="text-xs text-gray-400 truncate max-w-[200px] mt-0.5">
                                {form.formDescription}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-[#1B4582]/10 text-[#1B4582] rounded-full text-xs font-medium capitalize">
                            {form.formCategory || form.category || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700 font-medium">
                          {sectionCount}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700 font-medium">
                          {fieldCount}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={status} />
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-500 capitalize">
                            {form.formType || "custom"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400">
                          {updatedAt
                            ? new Date(updatedAt).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {/* View */}
                            <button
                              onClick={() => setViewingForm(form)}
                              title="View"
                              className="p-2 rounded-lg hover:bg-blue-50 text-blue-500 transition-colors"
                            >
                              <FiEye className="w-4 h-4" />
                            </button>
                            {/* Edit */}
                            <button
                              onClick={() => handleEditForm(form)}
                              title="Edit"
                              className="p-2 rounded-lg hover:bg-green-50 text-green-600 transition-colors"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            {/* Duplicate */}
                            <button
                              onClick={() => handleDuplicate(form)}
                              title="Duplicate"
                              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                            >
                              <FiCopy className="w-4 h-4" />
                            </button>
                            {/* Toggle active */}
                            <button
                              onClick={() => handleToggleStatus(form)}
                              title={isActive ? "Deactivate" : "Activate"}
                              className={`p-2 rounded-lg transition-colors ${
                                isActive
                                  ? "hover:bg-orange-50 text-orange-500"
                                  : "hover:bg-green-50 text-gray-400"
                              }`}
                            >
                              {isActive ? (
                                <FiToggleRight className="w-4 h-4" />
                              ) : (
                                <FiToggleLeft className="w-4 h-4" />
                              )}
                            </button>
                            {/* Delete */}
                            <button
                              onClick={() =>
                                setDeleteModal({ open: true, form })
                              }
                              title="Delete"
                              className="p-2 rounded-lg hover:bg-red-50 text-red-400 transition-colors"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Card grid (mobile) ── */}
            <div className="md:hidden space-y-3">
              {filteredForms.map((form, idx) => {
                const formId = form._id || form.id;
                const sectionCount = (form.sections || []).length;
                const fieldCount = (form.sections || []).reduce(
                  (acc, s) => acc + (s.fields || []).length,
                  0,
                );
                const status = form.status || "draft";
                const isActive = status === "active";

                return (
                  <div
                    key={formId || idx}
                    className="bg-white border border-gray-200 rounded-xl p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">
                          {form.formName || form.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5 capitalize">
                          {form.formCategory || "—"} · {sectionCount} sections ·{" "}
                          {fieldCount} fields
                        </p>
                      </div>
                      <StatusBadge status={status} />
                    </div>
                    <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                      <button
                        onClick={() => setViewingForm(form)}
                        className="p-2 rounded-lg hover:bg-blue-50 text-blue-500"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditForm(form)}
                        className="p-2 rounded-lg hover:bg-green-50 text-green-600"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(form)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                      >
                        <FiCopy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(form)}
                        className={`p-2 rounded-lg ${isActive ? "hover:bg-orange-50 text-orange-500" : "hover:bg-green-50 text-gray-400"}`}
                      >
                        {isActive ? (
                          <FiToggleRight className="w-4 h-4" />
                        ) : (
                          <FiToggleLeft className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => setDeleteModal({ open: true, form })}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-400"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── Create Form Drawer ── */}
      <CreateFormDrawer
        isOpen={showCreateDrawer}
        onClose={() => setShowCreateDrawer(false)}
        onCreateBoardingForm={handleCreateBoardingForm}
        onCreateCustomForm={handleCreateCustomForm}
        onboardingFormExists={forms.some(
          (f) =>
            f.formType === "predefined" ||
            String(f.formName || "")
              .trim()
              .toLowerCase() === "customer onboarding form",
        )}
      />

      {/* ── View Drawer ── */}
      {viewingForm && (
        <FormViewDrawer
          form={viewingForm}
          onClose={() => setViewingForm(null)}
        />
      )}

      {/* ── Delete Modal ── */}
      <DeleteModal
        open={deleteModal.open}
        formName={deleteModal.form?.formName || deleteModal.form?.name || ""}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteModal({ open: false, form: null })}
      />
    </ThemeMaker>
  );
}
